import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';

const PLAN = {
    name: 'Enterprise', tier: 'enterprise', status: 'active',
    billingCycle: 'annual', currency: 'USD', amount: 24000,
    nextBillingDate: 'Apr 1, 2026',
    limits: { users: 500, storage: 100, apiCalls: 500000, projects: 200 },
    usage: { users: 0, storage: 0, apiCalls: 0, projects: 0 },
};

const MODULES: any[] = [];

const BILLING_HISTORY: any[] = [];

export function Subscription() {
    const [activeTab, setActiveTab] = useState<'plan' | 'modules' | 'usage' | 'billing'>('plan');
    const [moduleToggles, setModuleToggles] = useState<Record<string, boolean>>(
        Object.fromEntries(MODULES.map(m => [m.id, m.enabled]))
    );

    const enabledModules = MODULES.filter(m => moduleToggles[m.id]);
    const monthlyBase = enabledModules.reduce((acc, m) => acc + m.monthlyPrice, 0) * PLAN.usage.users;

    return (
        <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-12">
            {/* Header Card */}
            <div className="p-4 rounded-xl border border-border bg-card flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0 shadow-xs">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
                        <CreditCard className="size-4.5" />
                    </div>
                    <div className="space-y-0.5 min-w-0">
                        <h1 className="tracking-tight text-foreground font-display text-page-title font-semibold truncate">Subscription &amp; Billing</h1>
                        <p className="text-xs text-muted-foreground leading-relaxed truncate max-w-4xl">Plan · Module Management · Usage Limits · Billing History</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto flex-wrap">
                    <Button variant="primary" onClick={() => toast.success('Opening upgrade dialog...')}>Upgrade Plan</Button>
                </div>
            </div>

            {/* Current Plan Banner */}
            <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-lg flex items-center justify-center text-2xl"></div>
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <p className="text-foreground font-display text-metric">{PLAN.name} Plan</p>
                                    <Badge variant="success">Active</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">Annual billing · ${(PLAN.amount / 12).toLocaleString()}/mo · Next renewal: {PLAN.nextBillingDate}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-primary font-display text-metric">${PLAN.amount.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">/year ({PLAN.currency})</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'plan', label: 'Plan Details' },
                    { id: 'modules', label: 'Module Management' },
                    { id: 'usage', label: 'Usage Limits' },
                    { id: 'billing', label: 'Billing History' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {activeTab === 'plan' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { name: 'Starter', price: 9, users: '1-10', color: 'from-text-600 to-text-700', current: false, features: ['People Hub', 'Work Hub', '10GB Storage', 'Email Support'] },
                        { name: 'Professional', price: 19, users: '11-200', color: '', current: false, features: ['All Starter +', 'Money Hub', 'Support Hub', '50GB Storage', 'Priority Support', 'API Access'] },
                        { name: 'Enterprise', price: 'Custom', users: '200+', color: '', current: true, features: ['All Professional +', 'Growth & Admin Hub', '100GB Storage', 'SSO / SAML', 'ABAC Policies', 'Audit Log', 'Dedicated CSM', 'SLA 99.9%'] },
                    ].map(plan => (
                        <Card key={plan.name} className={`relative ${plan.current ? 'border-primary/50 shadow-[0_0_24px_rgba(124,58,237,0.15)]' : ''}`}>
                            {plan.current && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-semibold rounded-full">Current Plan</div>}
                            <CardContent className="p-5">
                                <div className={`w-10 h-10 rounded-lg ${plan.color} flex items-center justify-center text-lg mb-3`}></div>
                                <p className={`font-semibold text-xl ${plan.current ? 'text-primary' : 'text-foreground'}`}>{plan.name}</p>
                                <p className="text-sm text-muted-foreground mb-3">Up to {plan.users} users</p>
                                <p className="text-foreground mb-4 font-display text-metric">{typeof plan.price === 'number' ? `$${plan.price}` : plan.price}<span className="text-sm font-normal text-muted-foreground">{typeof plan.price === 'number' ? '/user/mo' : ''}</span></p>
                                <div className="space-y-1.5 mb-4">
                                    {plan.features.map((f: any) => <div key={f} className="flex items-center gap-2 text-xs text-foreground"><span className="text-success"></span>{f}</div>)}
                                </div>
                                {!plan.current ? (
                                    <Button variant="secondary" size="sm" className="w-full" onClick={() => toast.success(`Switching to ${plan.name}...`)}>Contact Sales</Button>
                                ) : (
                                    <Button variant="primary" size="sm" className="w-full" onClick={() => toast.success('Managing plan...')}>Manage Plan</Button>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {activeTab === 'modules' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <p className="text-sm text-muted-foreground">Enable or disable hub modules. Changes take effect immediately for all users.</p>
                        <Button variant="primary" size="sm" onClick={() => toast.success('Module changes saved!')}>Save Changes</Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {MODULES.map(m => (
                            <Card key={m.id} className={`transition-all ${moduleToggles[m.id] ? 'border-primary/20' : 'opacity-70'}`}>
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4">
                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl shrink-0 ${moduleToggles[m.id] ? 'bg-primary/10 border border-primary/20' : 'bg-card border border-border'}`}>{m.icon}</div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <p className="font-semibold text-foreground">{m.name}</p>
                                                    {!m.enabled && <Badge variant="default">Add-on</Badge>}
                                                </div>
                                                <p className="text-xs text-muted-foreground mb-2">{m.description}</p>
                                                <div className="flex gap-1 flex-wrap">
                                                    {m.features.map((f: any) => <span key={f} className="text-xs px-1.5 py-0.5 bg-card border border-border rounded text-muted-foreground">{f}</span>)}
                                                </div>
                                                <p className="text-xs text-primary font-semibold mt-2">+${m.monthlyPrice}/user/mo</p>
                                            </div>
                                        </div>
                                        <Button variant="unstyled" size="unstyled" onClick={() => setModuleToggles(p => ({ ...p, [m.id]: !p[m.id] }))}>
                                            <div className={`w-12 h-6 rounded-full transition-colors shrink-0 ${moduleToggles[m.id] ? 'bg-primary' : 'bg-card border border-border'}`}>
                                                <div className={`mt-0.5 ml-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform ${moduleToggles[m.id] ? 'translate-x-6' : ''}`} />
                                            </div>
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'usage' && (
                <div className="space-y-4">
                    {[
                        { label: 'Users', used: PLAN.usage.users, limit: PLAN.limits.users, unit: '', icon: '', color: 'bg-primary' },
                        { label: 'Storage', used: PLAN.usage.storage, limit: PLAN.limits.storage, unit: ' GB', icon: '', color: 'bg-info' },
                        { label: 'API Calls / Month', used: PLAN.usage.apiCalls, limit: PLAN.limits.apiCalls, unit: '', icon: '', color: 'bg-warning' },
                        { label: 'Projects', used: PLAN.usage.projects, limit: PLAN.limits.projects, unit: '', icon: '', color: 'bg-success' },
                    ].map((u, i) => {
                        const pct = Math.round((u.used / u.limit) * 100);
                        return (
                            <Card key={i}>
                                <CardContent className="p-5">
                                    <div className="flex items-center gap-4">
                                        <span className="text-2xl">{u.icon}</span>
                                        <div className="flex-1">
                                            <div className="flex justify-between text-sm font-semibold mb-2">
                                                <span className="text-muted-foreground">{u.label}</span>
                                                <span className={pct >= 90 ? 'text-destructive' : pct >= 75 ? 'text-warning' : 'text-success'}>
                                                    {u.used.toLocaleString()}{u.unit} of {u.limit.toLocaleString()}{u.unit} ({pct}%)
                                                </span>
                                            </div>
                                            <div className="h-3 bg-background rounded-full overflow-hidden">
                                                <div className={`h-full ${u.color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                        {pct >= 80 && <Button variant="primary" size="sm" onClick={() => toast.success('Upgrade limit...')}>Upgrade</Button>}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {activeTab === 'billing' && (
                <div className="space-y-4">
                    <Card>
                        <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
                            <h2 className="text-foreground font-display text-section-title">Billing History</h2>
                            <Button variant="secondary" size="sm" onClick={() => toast.success('Updating payment method...')}>Update Payment Method</Button>
                        </CardHeader>
                        <CardContent className="p-0">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                        <th className="text-left px-6 py-3">Invoice</th>
                                        <th className="text-left px-4 py-3">Period</th>
                                        <th className="text-left px-4 py-3">Date</th>
                                        <th className="text-right px-4 py-3">Amount</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border-12">
                                    {BILLING_HISTORY.map(inv => (
                                        <tr key={inv.id} className="hover:bg-card/30">
                                            <td className="px-6 py-3 font-mono text-sm text-primary">{inv.invoiceNo}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{inv.period}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{inv.date}</td>
                                            <td className="px-4 py-3 text-right font-semibold text-foreground">${inv.amount.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-center"><Badge variant="success">Paid</Badge></td>
                                            <td className="px-4 py-3">
                                                <Button variant="secondary" size="sm" onClick={() => toast.success(`Downloading ${inv.invoiceNo}...`)}>PDF</Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}



